import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from 'react';
import {
  AuthContextType,
  LoginAPIRequest,
  LoginAPIResponse,
  RefreshTokenAPIResponse,
} from './types';
import { Nullable } from 'shared/types/global';
import axios from 'axios';
import { env } from 'core/env';
import { getNetworkErrorInfo } from 'functions/getNetworkErrorInfo';
import toast from 'react-hot-toast';
import showSomethingWentWrongToast from 'functions/showSomethingWentWrongToast';
import {
  clearAllTokensInLocalStorage,
  setTokensToLocalStorage,
  getTokensFromLocalStorage,
} from 'functions/localStorageTokensHelpers';
import { GetMyTeamInfoAPIResponse } from 'hooks/query/useUserQueries/types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthContextType['user']>(null);
  const [userTeam, setUserTeam] = useState<AuthContextType['userTeam']>(null);
  const [teammates, setTeammates] = useState<AuthContextType['teammates']>([]);
  const [totalVisitsCount, setTotalVisitsCount] =
    useState<AuthContextType['totalVisitsCount']>(0);

  const [token, setToken] = useState<Nullable<string>>(null);
  const [refreshToken, setRefreshToken] = useState<Nullable<string>>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  const getMyTeamInfo = useCallback(
    async (
      userToken: typeof token
    ): Promise<Nullable<GetMyTeamInfoAPIResponse>> => {
      try {
        const response = await axios.get<GetMyTeamInfoAPIResponse>(
          env.API_URL_SCOREBOARD_ME,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${userToken}`,
            },
          }
        );

        return response.data;
      } catch (error) {
        const errorInfo = getNetworkErrorInfo(error);
        console.error('Team info error:', errorInfo.message);
        return null;
      }
    },
    []
  );

  const refresh = async (): Promise<Nullable<string>> => {
    try {
      const tokens = getTokensFromLocalStorage();

      if (!tokens.refreshToken) {
        return null;
      }

      const response = await axios.post<RefreshTokenAPIResponse>(
        `${env.API_URL_SCOREBOARD}/api/v1/scoreboard/refresh`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokens.refreshToken}`,
          },
        }
      );

      const newAccessToken = response.data.token;

      setTokensToLocalStorage(newAccessToken, tokens.refreshToken);
      setToken(newAccessToken);

      return newAccessToken;
    } catch (error) {
      console.error('Refresh token failed:', error);
      clearAllTokensInLocalStorage();
      setToken(null);
      setRefreshToken(null);
      return null;
    }
  };

  const initialize = useCallback(async (): Promise<void> => {
    try {
      setIsInitializing(true);

      const tokens = getTokensFromLocalStorage();

      if (!tokens.accessToken || !tokens.refreshToken) {
        setIsInitializing(false);
        return;
      }

      setToken(tokens.accessToken);
      setRefreshToken(tokens.refreshToken);

      let teamData = await getMyTeamInfo(tokens.accessToken);

      if (!teamData) {
        const newAccessToken = await refresh();

        if (!newAccessToken) {
          setIsInitializing(false);
          return;
        }

        teamData = await getMyTeamInfo(newAccessToken);
      }

      if (!teamData) {
        clearAllTokensInLocalStorage();
        setToken(null);
        setRefreshToken(null);
        setIsInitializing(false);
        return;
      }

      const { total_visits, users } = teamData;

      const currentUser = users.find(user => user.is_current_user) ?? null;
      const teammates = users.filter(user => user.id !== currentUser?.id);

      setUser(currentUser);
      setUserTeam(teamData);
      setTeammates(teammates);
      setTotalVisitsCount(total_visits);
    } catch (error) {
      console.error('Initialize error:', error);
      clearAllTokensInLocalStorage();
      setToken(null);
      setRefreshToken(null);
      setUser(null);
      setTeammates([]);
      setTotalVisitsCount(0);
    } finally {
      setIsInitializing(false);
    }
  }, [getMyTeamInfo]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const login = async (data: LoginAPIRequest): Promise<boolean> => {
    try {
      const res = await axios.post<LoginAPIResponse>(
        env.API_URL_SCOREBOARD_LOGIN,
        data
      );

      const { user_id, access_token, refresh_token } = res.data;

      const teamData = await getMyTeamInfo(access_token);

      if (!teamData) {
        showSomethingWentWrongToast();
        return false;
      }

      const { total_visits, users } = teamData;
      const currentUser = users.find(user => user.id === user_id);

      if (!currentUser) {
        showSomethingWentWrongToast();
        return false;
      }

      const teammates = users.filter(user => user.id !== user_id);

      setTokensToLocalStorage(access_token, refresh_token);

      setUser(currentUser);
      setUserTeam(teamData);
      setTeammates(teammates);
      setToken(access_token);
      setRefreshToken(refresh_token);
      setTotalVisitsCount(total_visits);
      return true;
    } catch (error) {
      const errorInfo = getNetworkErrorInfo(error);
      let errorMessage: string;

      if (typeof errorInfo.message === 'string') {
        errorMessage = errorInfo.message;
      } else {
        const firstError = errorInfo.message[0];
        errorMessage =
          firstError?.msg || 'Please check your input and try again';
      }

      toast.error(errorMessage);
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      clearAllTokensInLocalStorage();

      setUser(null);
      setTeammates([]);
      setTotalVisitsCount(0);
      setToken(null);
      setRefreshToken(null);

      toast.success('Successful logout');
    } catch (error) {
      console.error('Logout error:', error);
      clearAllTokensInLocalStorage();
      setUser(null);
      setTeammates([]);
      setTotalVisitsCount(0);
      setToken(null);
      setRefreshToken(null);

      showSomethingWentWrongToast();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        // State
        token,
        refreshToken,
        user,
        userTeam,
        isInitializing,
        teammates,
        totalVisitsCount,

        // Setters
        setUser,
        setToken,
        setRefreshToken,

        // Actions
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
